import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import HandymanOutlinedIcon from "@mui/icons-material/HandymanOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PageLayout from "../../components/layout/PageLayout";
import { toast } from "react-toastify";
import { ORDER_LIST_PATH } from "../../navigations/path";
import { useGetOrderByIdQuery } from "../../store/api";

const display = (value) =>
  value !== null && value !== undefined && String(value).trim() !== ""
    ? value
    : "—";

const hasValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

// SaleTypeEnum / BillingCycleEnum values are hyphenated (front-sale, one-time).
const formatSaleType = (value) =>
  String(value || "")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const statusChipColor = (status) => {
  const lower = String(status || "").toLowerCase();
  if (["paid", "completed", "success", "active"].some((x) => lower.includes(x)))
    return "success";
  if (["unpaid", "partial", "pending"].some((x) => lower.includes(x)))
    return "warning";
  if (
    ["cancelled", "canceled", "voided", "failed"].some((x) => lower.includes(x))
  )
    return "error";
  if (["refunded", "chargeback"].some((x) => lower.includes(x))) return "info";
  return "default";
};

const statusHint = (status) => {
  const lower = String(status || "").toLowerCase();
  if (lower === "unpaid")
    return "This order is unpaid. Use an invoice payment link below to collect payment.";
  if (lower === "partial")
    return "This order is partially paid. Remaining dues may still need collection.";
  if (lower === "refunded") return "This order was refunded.";
  if (lower === "chargeback") return "This order has a chargeback.";
  if (lower === "cancelled" || lower === "canceled" || lower === "voided")
    return "This order is no longer active.";
  return null;
};

const copyText = async (text, successMessage) => {
  if (!hasValue(text)) {
    toast.error("Nothing to copy");
    return;
  }
  try {
    await navigator.clipboard.writeText(String(text));
    toast.success(successMessage);
  } catch {
    toast.error("Failed to copy");
  }
};

const InfoItem = ({
  label,
  value,
  fullWidth = false,
  copyValue,
  copyLabel,
}) => (
  <Grid item xs={12} sm={fullWidth ? 12 : 6} md={fullWidth ? 12 : 4}>
    <Typography
      variant="caption"
      color="text.secondary"
      display="block"
      sx={{ mb: 0.35 }}
    >
      {label}
    </Typography>
    <Stack direction="row" spacing={0.5} alignItems="flex-start">
      <Typography
        variant="body1"
        sx={{ fontWeight: 500, wordBreak: "break-word", flex: 1, minWidth: 0 }}
      >
        {display(value)}
      </Typography>
      {hasValue(copyValue) ? (
        <Tooltip title={copyLabel || `Copy ${label.toLowerCase()}`}>
          <IconButton
            size="small"
            onClick={() => copyText(copyValue, copyLabel || `${label} copied`)}
            aria-label={copyLabel || `Copy ${label}`}
          >
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Stack>
  </Grid>
);

const ContactField = ({ label, value, href, onCopy, copyTitle }) => (
  <Grid item xs={12}>
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ mb: 0.35 }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {hasValue(value) ? (
        <Tooltip title={copyTitle}>
          <IconButton
            size="small"
            onClick={onCopy}
            aria-label={copyTitle}
            sx={{ color: "text.secondary" }}
          >
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Stack>
    {hasValue(value) ? (
      <Link
        href={href}
        underline="hover"
        sx={{
          fontWeight: 500,
          wordBreak: "break-word",
          display: "inline-block",
          maxWidth: "100%",
        }}
      >
        {value}
      </Link>
    ) : (
      <Typography sx={{ fontWeight: 500 }}>—</Typography>
    )}
  </Grid>
);

const SectionCard = ({ icon, title, subtitle, children, action }) => (
  <Card
    sx={{
      mb: 2.5,
      borderRadius: 3,
      border: "1px solid",
      borderColor: "divider",
    }}
    elevation={0}
  >
    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 2,
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: "primary.main",
              color: "common.white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              "& svg": { fontSize: 22 },
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

const EmptyState = ({ icon, message }) => (
  <Box
    sx={{
      py: 3,
      px: 2,
      textAlign: "center",
      borderRadius: 2,
      bgcolor: "grey.50",
      border: "1px dashed",
      borderColor: "divider",
    }}
  >
    <Box sx={{ color: "text.disabled", mb: 1, "& svg": { fontSize: 32 } }}>
      {icon}
    </Box>
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
  </Box>
);

const SummaryStat = ({ label, value }) => (
  <Paper
    variant="outlined"
    sx={{
      px: 2,
      py: 1.75,
      borderRadius: 2.5,
      height: "100%",
      bgcolor: "background.paper",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      minHeight: 78,
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      display="block"
      sx={{ mb: 0.5, lineHeight: 1.2 }}
    >
      {label}
    </Typography>
    <Tooltip
      title={typeof value === "string" && value.length > 28 ? value : ""}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          lineHeight: 1.25,
          wordBreak: "break-word",
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          fontSize: { xs: "1rem", sm: "1.15rem" },
        }}
      >
        {value}
      </Typography>
    </Tooltip>
  </Paper>
);

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useGetOrderByIdQuery(
    // GET /orders/{id} eager-loads its relations server-side (OrderController::show),
    // so sending `relations` here does nothing.
    { id },
    { skip: !id },
  );

  const order = data?.data ?? data ?? null;
  console.log("order==>", order);
  useEffect(() => {
    if (isError) {
      toast.error(error?.data?.message || "Failed to fetch order details");
      navigate(ORDER_LIST_PATH);
    }
  }, [isError, error, navigate]);

  const services = useMemo(
    () => (Array.isArray(order?.services) ? order.services : []),
    [order],
  );
  const subscriptions = useMemo(
    () => (Array.isArray(order?.subscriptions) ? order.subscriptions : []),
    [order],
  );
  const invoices = useMemo(
    () => (Array.isArray(order?.invoices) ? order.invoices : []),
    [order],
  );

  const addressLine = useMemo(() => {
    if (!order) return "";
    return [order.address, order.city, order.state, order.zip]
      .filter(hasValue)
      .join(", ");
  }, [order]);

  if (isLoading) {
    return (
      <PageLayout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={360}
        >
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (!order) {
    return (
      <PageLayout>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            onClick={() => navigate(ORDER_LIST_PATH)}
          >
            Back to Orders
          </Button>
          <Alert severity="info" sx={{ mt: 3 }}>
            No order found.
          </Alert>
        </Container>
      </PageLayout>
    );
  }

  const currencySymbol = order.currency?.symbol || "$";
  const amountDisplay = `${currencySymbol}${display(order.amount)}`;
  const hint = statusHint(order.status);
  const firstPaymentUrl = invoices.find((inv) =>
    hasValue(inv.payment_url),
  )?.payment_url;

  return (
    <PageLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2.5,
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            onClick={() => navigate(ORDER_LIST_PATH)}
          >
            Back to Orders
          </Button>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Chip
              label={formatSaleType(order.status)}
              color={statusChipColor(order.status)}
              sx={{ fontWeight: 600 }}
            />
            {/* <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={() => copyText(order.id, "Order ID copied")}
            >
              Copy order ID
            </Button> */}
            {firstPaymentUrl ? (
              <Button
                variant="contained"
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={() => copyText(firstPaymentUrl, "Payment link copied")}
              >
                Copy payment link
              </Button>
            ) : null}
          </Stack>
        </Box>

        {hint ? (
          <Alert
            severity={
              statusChipColor(order.status) === "error" ? "error" : "warning"
            }
            sx={{ mb: 2.5 }}
          >
            {hint}
          </Alert>
        ) : null}

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 3.5 },
            mb: 2.5,
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
            background:
              "linear-gradient(135deg, rgba(25,118,210,0.10) 0%, rgba(25,118,210,0.02) 55%)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2.5}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  bgcolor: "primary.main",
                  color: "common.white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <ShoppingBagOutlinedIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ lineHeight: 1 }}
                >
                  Order details
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: "primary.main",
                    wordBreak: "break-word",
                  }}
                >
                  {hasValue(order.lead?.name) ? order.lead.name : "Order"}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {formatSaleType(order.sale_type)} ·{" "}
                  {display(order.created_at)}
                </Typography>
              </Box>
            </Stack>

            <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Total amount
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  fontSize: { xs: "1.75rem", sm: "2.25rem" },
                }}
              >
                {amountDisplay}
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          <Grid container spacing={1.5}>
            <Grid item xs={6} sm={3}>
              <SummaryStat label="Services" value={services.length} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryStat label="Subscriptions" value={subscriptions.length} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryStat label="Invoices" value={invoices.length} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryStat
                label="Payment account"
                value={hasValue(order.account?.name) ? order.account.name : "—"}
              />
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={0}>
          <Grid item xs={12} md={6} sx={{ pr: { md: 1.25 } }}>
            <SectionCard
              icon={<PersonOutlineIcon />}
              title="Customer"
              subtitle="Lead contact information"
            >
              <Grid container spacing={2}>
                <InfoItem label="Name" value={order.lead?.name} fullWidth />
                <ContactField
                  label="Email"
                  value={order.lead?.email}
                  href={`mailto:${order.lead?.email}`}
                  copyTitle="Copy email"
                  onCopy={() => copyText(order.lead.email, "Email copied")}
                />
                <ContactField
                  label="Phone"
                  value={order.lead?.phone}
                  href={`tel:${order.lead?.phone}`}
                  copyTitle="Copy phone"
                  onCopy={() => copyText(order.lead.phone, "Phone copied")}
                />
                <InfoItem
                  label="Lead notes"
                  value={order.lead?.description}
                  fullWidth
                />
              </Grid>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6} sx={{ pl: { md: 1.25 } }}>
            <SectionCard
              icon={<PaymentsOutlinedIcon />}
              title="Payment setup"
              subtitle="How this order is billed"
            >
              <Grid container spacing={2}>
                <InfoItem label="Payment account" value={order.account?.name} />
                <InfoItem
                  label="Payment type"
                  value={order.payment_type?.name}
                />
                <InfoItem
                  label="Currency"
                  value={
                    order.currency
                      ? `${order.currency.name || order.currency.code || ""}`.trim()
                      : null
                  }
                />
                <InfoItem label="Brand" value={order?.brand?.name} />
                <InfoItem
                  label="Descriptor"
                  value={order?.account?.descriptor}
                />
              </Grid>
            </SectionCard>
          </Grid>

          <Grid item xs={12}>
            <SectionCard
              icon={<LocationOnOutlinedIcon />}
              title="Order & location"
              subtitle="Sale type and billing address"
            >
              <Grid container spacing={2}>
                <InfoItem
                  label="Sale type"
                  value={formatSaleType(order.sale_type)}
                />
                <InfoItem label="Created at" value={order.created_at} />
                <InfoItem label="City" value={order.city} />
                <InfoItem label="State" value={order.state} />
                <InfoItem label="Zip" value={order.zip} />
                <InfoItem
                  label="Full address"
                  value={addressLine || order.address}
                  fullWidth
                  copyValue={addressLine || order.address}
                  copyLabel="Address copied"
                />
                <InfoItem
                  label="Order description"
                  value={order.description}
                  fullWidth
                />
              </Grid>
            </SectionCard>
          </Grid>

          <Grid item xs={12}>
            <SectionCard
              icon={<HandymanOutlinedIcon />}
              title="Services"
              subtitle={
                services.length
                  ? `${services.length} service${services.length === 1 ? "" : "s"} on this order`
                  : "No services attached"
              }
              action={
                <Chip size="small" label={services.length} variant="outlined" />
              }
            >
              {services.length ? (
                <Grid container spacing={2}>
                  {services.map((item, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      key={item.id ?? `${item.service?.id}-${index}`}
                    >
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2.5,
                          height: "100%",
                          transition: "border-color 0.15s ease",
                          "&:hover": { borderColor: "primary.light" },
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          gap={1}
                          sx={{ mb: 1 }}
                        >
                          <Typography fontWeight={700}>
                            {display(item.service?.name)}
                          </Typography>
                          <Chip
                            size="small"
                            label={item.is_recurring ? "Recurring" : "One-time"}
                            color={item.is_recurring ? "info" : "default"}
                            variant="outlined"
                          />
                        </Stack>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, mb: 0.75 }}
                        >
                          {currencySymbol}
                          {display(item.price)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {display(item.service?.description)}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <EmptyState
                  icon={<Inventory2OutlinedIcon />}
                  message="No services found for this order."
                />
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12}>
            <SectionCard
              icon={<AutorenewOutlinedIcon />}
              title="Subscriptions"
              subtitle={
                subscriptions.length
                  ? `${subscriptions.length} active billing cycle record${subscriptions.length === 1 ? "" : "s"}`
                  : "No subscriptions yet"
              }
              action={
                <Chip
                  size="small"
                  label={subscriptions.length}
                  variant="outlined"
                />
              }
            >
              {subscriptions.length ? (
                <Grid container spacing={2}>
                  {subscriptions.map((item) => (
                    <Grid item xs={12} sm={6} key={item.id}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, borderRadius: 2.5, height: "100%" }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          gap={1}
                          sx={{ mb: 1 }}
                        >
                          <Typography fontWeight={700}>
                            {display(item.service?.name)}
                          </Typography>
                          <Chip
                            size="small"
                            label={formatSaleType(item.status)}
                            color={statusChipColor(item.status)}
                          />
                        </Stack>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" color="text.secondary">
                            Billing cycle:{" "}
                            <b>{formatSaleType(item.billing_cycle)}</b>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Price:{" "}
                            <b>
                              {currencySymbol}
                              {display(item.price)}
                            </b>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Created: {display(item.created_at)}
                          </Typography>
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <EmptyState
                  icon={<AutorenewOutlinedIcon />}
                  message="No subscriptions found for this order."
                />
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12}>
            <SectionCard
              icon={<ReceiptLongOutlinedIcon />}
              title="Invoices"
              subtitle={
                invoices.length
                  ? "Copy a payment link to share with the customer"
                  : "No invoices generated yet"
              }
              action={
                <Chip size="small" label={invoices.length} variant="outlined" />
              }
            >
              {invoices.length ? (
                <Grid container spacing={2}>
                  {invoices.map((invoice) => (
                    <Grid item xs={12} sm={6} key={invoice.id}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2.25, borderRadius: 2.5, height: "100%" }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          gap={1}
                          sx={{ mb: 1.25 }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {currencySymbol}
                            {display(invoice.amount)}
                          </Typography>
                          <Chip
                            size="small"
                            label={formatSaleType(invoice.status)}
                            color={statusChipColor(invoice.status)}
                          />
                        </Stack>

                        <Stack spacing={0.65} sx={{ mb: 1.75 }}>
                          <Typography variant="body2" color="text.secondary">
                            Expires: {display(invoice.expires_at)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Payment attempts:{" "}
                            {display(invoice.payment_attempts)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Note: {display(invoice.note)}
                          </Typography>
                        </Stack>

                        {hasValue(invoice.payment_url) ? (
                          <>
                            <Divider sx={{ mb: 1.5 }} />
                            <Button
                              fullWidth
                              variant="outlined"
                              size="small"
                              startIcon={<ContentCopyIcon />}
                              onClick={() =>
                                copyText(
                                  invoice.payment_url,
                                  "Payment link copied",
                                )
                              }
                            >
                              Copy payment link
                            </Button>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ mt: 1, wordBreak: "break-all" }}
                            >
                              {invoice.payment_url}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No payment link available
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <EmptyState
                  icon={<ReceiptLongOutlinedIcon />}
                  message="No invoices found for this order."
                />
              )}
            </SectionCard>
          </Grid>
        </Grid>
      </Container>
    </PageLayout>
  );
};

export default OrderDetails;
